import * as Yup from "yup";
import { BoletagemFlexSchema } from "../boletagem-flex";
import { BoletagemPgtoDiferenciadoSchema } from "../boletagem-pgto-diferenciado";
import { BoletagemProdutoFaturamento, BoletagemProdutoHabilitadoNetFinanceiro, BoletagemProdutoLiberarFaturamento, BoletagemProdutoLiberarRegistro, BoletagemProdutoPagamento } from "./model";
import { GrandezaEnergia } from "../boletagem/model";

export class BoletagemProdutoSchema {
  public static id() {
    return recordShape.id;
  }

  public static record() {
    return Yup
      .object()
      .shape(recordShape);
  }

  public static createForm() {
    const {
      grandeza,
      anoMes,
      volumeEnergiaHora,
      volumeEnergiaMedio,
      preco,
    } = recordShape;

    const createFormShape = {
      grandeza: grandeza.required("Grandeza é obrigatória"),
      anoMes: anoMes.required("Mês é obrigatório"),
      volumeEnergiaHora,
      volumeEnergiaMedio,
      preco: preco.required("Preço é obrigatório"),
    };

    return Yup
      .object()
      .shape(createFormShape);
  }

  public static updateForm() {
    const {
      id,
      grandeza,
      anoMes,
      volumeEnergiaHora,
      volumeEnergiaMedio,
      preco,
    } = recordShape;

    const updateFormShape = {
      id,
      grandeza: grandeza.required("Grandeza é obrigatória"),
      anoMes: anoMes.required("Mês é obrigatório"),
      volumeEnergiaHora,
      volumeEnergiaMedio,
      preco: preco.required("Preço é obrigatório"),
    };

    return Yup
      .object()
      .shape(updateFormShape);
  }

  public static updateFlexibilidade() {
    const {
      volumeTotal,
      valorTotal,
      flexibilidades,
    } = recordShape;
    return Yup
      .object()
      .shape({
        volumeTotal: volumeTotal.required("Volume Total é obrigatório"),
        valorTotal: valorTotal.required("Valor Total é obrigatório"),
        flexibilidades: flexibilidades.required("Flexibilidade é obrigatório"),
      });
  }

  public static updateFinanceiro() {
    const {
      liberarFaturamento,
      liberarRegistro,
      faturamento,
      pagamento,
      multa,
      NF,
      vencimentoNF,
    } = recordShape;
    return Yup
      .object()
      .shape({
        liberarFaturamento: liberarFaturamento.optional(),
        liberarRegistro: liberarRegistro.optional(),
        faturamento: faturamento.optional(),
        pagamento: pagamento.optional(),
        multa: multa.optional(),
        NF: NF.optional(),
        vencimentoNF: vencimentoNF.optional(),
      });
  }

  public static liberarFaturamento() {
    const {
      pgtosDiferenciados,
      pgtoDiferenciado,
    } = recordShape;
    return Yup
      .object()
      .shape({
        pgtoDiferenciado: pgtoDiferenciado.optional(),
        pgtosDiferenciados: pgtosDiferenciados.optional(),
      });
  }

  public static updateHabilitadoNetFinanceiro() {
    const {
      id,
    } = recordShape;

    const updateFormShape = {
      id,
      habilitadoNetFinanceiro: Yup
        .mixed<BoletagemProdutoHabilitadoNetFinanceiro>()
        .oneOf(
          Object.values(BoletagemProdutoHabilitadoNetFinanceiro),
          `Habilitado Net Financeiro deve ser "${Object.values(BoletagemProdutoHabilitadoNetFinanceiro).join('" ou "')}"`
        )
        .required("Habilitado Net Financeiro é obrigatório"),
    };

    return Yup
      .object()
      .shape(updateFormShape);
  }
}

const recordShape = {
  id: Yup
    .number(),
  volumeTotal: Yup
    .number()
    .typeError("Valor inválido volume total"),
  valorTotal: Yup
    .number()
    .typeError("Valor inválido para valor total"),
  flexibilidades: Yup.array()
    .of(BoletagemFlexSchema.record()),
  pgtoDiferenciado: Yup.boolean().default(false),
  pgtosDiferenciados: Yup.array()
    .of(BoletagemPgtoDiferenciadoSchema.record()),
  liberarRegistro: Yup
    .mixed<BoletagemProdutoLiberarRegistro>()
    .oneOf(
      Object.values(BoletagemProdutoLiberarRegistro),
      `Liberar registro deve ser "${Object.values(BoletagemProdutoLiberarRegistro).join('" ou "')}"`
    ),
  liberarFaturamento: Yup
    .mixed<BoletagemProdutoLiberarFaturamento>()
    .oneOf(
      Object.values(BoletagemProdutoLiberarFaturamento),
      `Liberar faturamento deve ser "${Object.values(BoletagemProdutoLiberarFaturamento).join('" ou "')}"`
    ),
  faturamento: Yup
    .mixed<BoletagemProdutoFaturamento>()
    .oneOf(
      Object.values(BoletagemProdutoFaturamento),
      `Faturamento deve ser "${Object.values(BoletagemProdutoFaturamento).join('" ou "')}"`
    ),
  pagamento: Yup
    .mixed<BoletagemProdutoPagamento>()
    .oneOf(
      Object.values(BoletagemProdutoPagamento),
      `Pagamento deve ser "${Object.values(BoletagemProdutoPagamento).join('" ou "')}"`
    ),
  multa: Yup
    .number()
    .typeError("Valor inválido para multa"),
  NF: Yup
    .string()
    .matches(/^\d*$/u)
    .typeError("Valor inválido para NF"),
  vencimentoNF: Yup
    .string()
    .transform((value) => (value ? value : null))
    .matches(/^\d{4}-\d{2}-\d{2}$/u, "Vencimento NF com formato inválido")
    .typeError("Valor inválido para Vencimento NF"),
  grandeza: Yup.mixed<GrandezaEnergia>().oneOf(Object.values(GrandezaEnergia), `Grandeza deve ser "${Object.values(GrandezaEnergia).join('" ou "')}"`),
  anoMes: Yup
    .string()
    .matches(/^\d{4}-(?:0[1-9]|1[0-2])$/u, "Mês com formato inválido")
    .typeError("Valor inválido para mês"),
  volumeEnergiaMedio: Yup
    .mixed()
    .when("grandeza", {
      is: (grandeza: GrandezaEnergia) => grandeza === GrandezaEnergia.MWm,
      then: Yup.number().required("Campo obrigatório"),
      otherwise: Yup.number().nullable(),
    }),
  volumeEnergiaHora: Yup
    .mixed()
    .when("grandeza", {
      is: (grandeza: GrandezaEnergia) => grandeza === GrandezaEnergia.MWh,
      then: Yup.number().required("Campo obrigatório"),
      otherwise: Yup.number().nullable(),
    }),
  preco: Yup.number(),
};

// const produtoFormRecordShape
